import { clsx } from 'clsx'
import * as React from 'react'
import { useContext, useId } from 'react'
import type { ChangeEventHandler } from 'react'

import { CheckIcon } from '../icons'
import { VisuallyHidden } from '../VisuallyHidden'
import { TAG_COLORS, LEGACY_COLOR_MAP } from '../Tag/constants'
import type { TagColor } from '../Tag/constants'
import { generateSwatchCssProperties } from './helpers'

import styles from './SwatchGroup.module.css'

type NamedColor = TagColor

const namedColorSet = new Set<string>(TAG_COLORS)

function isNamedColor(color: string): color is TagColor {
  return namedColorSet.has(color)
}

export interface SwatchProps {
  /**
   * Color value in hex, rgb, or hsl
   */
  color: NamedColor | string
  /**
   * Name of the color, it is used as a label for the swatch
   */
  name: string
}

interface SwatchGroupProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  /**
   * Value of the selected color, it is used to set the value of the input
   */
  value?: string
  onChange?: ChangeEventHandler<HTMLInputElement>
}

const Swatch: React.FC<SwatchProps> = ({ color, name }) => {
  const { value, onChange, id, ariaDescribedby } =
    useContext(SwatchGroupContext)
  const selected = value === color
  const isLegacyColor = LEGACY_COLOR_MAP[color] !== undefined

  const isColorNamed = isNamedColor(color)
  const swatchId = `${id}-${name}-swatch`

  return (
    <label htmlFor={swatchId}>
      <VisuallyHidden>{name}</VisuallyHidden>
      <input
        type="radio"
        id={swatchId}
        name={id}
        value={color}
        checked={selected}
        onChange={onChange}
        className={styles.input}
        aria-describedby={ariaDescribedby}
      />
      <span
        className={clsx(
          styles.circle,
          isColorNamed && styles[color],
          isLegacyColor && styles[LEGACY_COLOR_MAP[color]],
        )}
        style={
          !isColorNamed && !isLegacyColor
            ? generateSwatchCssProperties(color)
            : undefined
        }
      >
        {selected && <CheckIcon color="currentColor" />}
      </span>
    </label>
  )
}

const SwatchGroupContext = React.createContext<{
  value?: string
  onChange?: ChangeEventHandler<HTMLInputElement>
  id: string
  ariaDescribedby?: string
}>({ id: '' })

const SwatchGroup: React.FC<SwatchGroupProps> & {
  Swatch: React.FC<SwatchProps>
} = ({ value, onChange, className, children, ...props }: SwatchGroupProps) => {
  const id = useId()
  const ariaDescribedby = props['aria-describedby']

  return (
    <SwatchGroupContext.Provider
      value={{ value, onChange, id, ariaDescribedby }}
    >
      <div className={clsx(styles.base, className)} {...props}>
        {children}
      </div>
    </SwatchGroupContext.Provider>
  )
}

SwatchGroup.Swatch = Swatch

export { SwatchGroup }
